import React from 'react';
import { Box, alpha, useTheme } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { borderRadius } from '../../../theme/theme';
import { clampProgressPercent } from './operationProgressUtils';
import { usePrefersReducedMotion } from './usePrefersReducedMotion';

export interface OperationProgressIndicatorProps {
  size?: number | string;
  color?: string;
  thickness?: number;
  ariaLabel?: string;
  /**
   * Set when the surrounding UI already announces the phase in words. The ring
   * then becomes pure decoration so screen readers hear the phase once.
   */
  presentational?: boolean;
  sx?: SxProps<Theme>;
}

export interface ProgressMeterProps {
  value: number;
  tone?: 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info';
  ariaLabel?: string;
  /**
   * Set when the surrounding UI already says how far along it is in words. The
   * bar then becomes pure decoration so screen readers hear the position once.
   */
  presentational?: boolean;
  /** Names this meter when a caller has more than one on screen. */
  testId?: string;
  height?: number | string;
  sx?: SxProps<Theme>;
}

const meterColor = (theme: Theme, tone: ProgressMeterProps['tone'] = 'primary'): string => {
  switch (tone) {
    case 'success':
      return theme.palette.success.main;
    case 'warning':
      return theme.palette.warning.main;
    case 'error':
      return theme.palette.error.main;
    case 'info':
      return theme.palette.info.main;
    case 'default':
      return theme.palette.text.secondary;
    case 'primary':
    default:
      return theme.palette.primary.main;
  }
};

export const OperationProgressIndicator: React.FC<OperationProgressIndicatorProps> = ({
  size = 16,
  color = 'currentColor',
  thickness = 2,
  ariaLabel = 'Loading',
  presentational = false,
  sx,
}) => {
  const resolvedSize = typeof size === 'number' ? `${size}px` : size;
  const reducedMotion = usePrefersReducedMotion();

  return (
    <Box
      component="span"
      role={presentational ? undefined : 'status'}
      aria-label={presentational ? undefined : ariaLabel}
      aria-hidden={presentational ? true : undefined}
      data-testid="operation-progress-indicator"
      data-reduced-motion={String(reducedMotion)}
      sx={{
        '@keyframes operation-progress-spin': {
          to: { transform: 'rotate(360deg)' },
        },
        width: resolvedSize,
        height: resolvedSize,
        display: 'inline-block',
        flexShrink: 0,
        borderRadius: '50%',
        color,
        border: `${Math.max(1, thickness)}px solid currentColor`,
        borderRightColor: 'transparent',
        // Without animation the ring is a static outline; the caller's words
        // carry the meaning, so nothing is lost.
        animation: reducedMotion ? 'none' : 'operation-progress-spin 0.75s linear infinite',
        ...sx,
      }}
    />
  );
};

export const ProgressMeter: React.FC<ProgressMeterProps> = ({
  value,
  tone = 'primary',
  ariaLabel = 'Progress',
  presentational = false,
  testId = 'progress-meter',
  height = 6,
  sx,
}) => {
  const theme = useTheme();
  const clampedValue = clampProgressPercent(value) ?? 0;
  const accentColor = meterColor(theme, tone);
  const resolvedHeight = typeof height === 'number' ? `${height}px` : height;
  const reducedMotion = usePrefersReducedMotion();

  return (
    <Box
      role={presentational ? undefined : 'progressbar'}
      aria-label={presentational ? undefined : ariaLabel}
      aria-valuemin={presentational ? undefined : 0}
      aria-valuemax={presentational ? undefined : 100}
      aria-valuenow={presentational ? undefined : clampedValue}
      aria-hidden={presentational ? true : undefined}
      data-testid={testId}
      data-progress-percent={String(clampedValue)}
      data-reduced-motion={String(reducedMotion)}
      sx={{
        position: 'relative',
        width: '100%',
        height: resolvedHeight,
        overflow: 'hidden',
        borderRadius: `${borderRadius.full}px`,
        backgroundColor: alpha(accentColor, 0.14),
        ...sx,
      }}
    >
      <Box
        sx={{
          width: `${clampedValue}%`,
          height: '100%',
          borderRadius: 'inherit',
          backgroundColor: accentColor,
          // Without the slide the bar simply appears at its new length; the
          // position is unchanged, only the travel to it is dropped.
          transition: reducedMotion ? 'none' : 'width 180ms ease-out',
        }}
      />
    </Box>
  );
};
